import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { useEffect, useState } from "react";
import { getCourseByPaginationApi, deleteCourseApi } from "../../../Api/Course";
import toast from "react-hot-toast";
import DeleteItem from "../DeleteItem";
import { IconTrash } from "@tabler/icons-react";
import { CourseForm } from "./CourseForm";
import SideModal from "../../general/Sidemodal";

interface Props {
  url: string;
}
interface CourseInterface {
  id: string;
  name: string;
  gender: number;
}

export function Course({ url }: Props) {
  const [courses, setCourses] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: string } | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const itemsPerPage = 10;


  const navigate = useNavigate();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editCourseId, setEditCourseId] = useState<string | null>(null);

  const handleAddClick = () => {
    setEditCourseId(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (id: string) => {
    setEditCourseId(id);
    setIsFormModalOpen(true);
  };



  const handleDeleteClick = (id: string) => {
    setSelectedItem({ id });
    setIsModalOpen(true);
  };


  const confirmDelete = async () => {
    if (!selectedItem) return;

    try {
      await deleteCourseApi(selectedItem.id, url);
      handleApiCallClick(currentPage);
      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete." + error);
    } finally {
      setIsModalOpen(false);
      setSelectedItem(null);
    }
  };

  const handleApiCallClick = async (
    page: number
  ) => {
    try {
      const courseData: any = await getCourseByPaginationApi(url, page, itemsPerPage, searchTerm);

      // Process each course data to handle the gender based on the value
      const processedCourses = courseData.course.map((course: CourseInterface) => {
        let targetGender = "All";

        if (course.gender === null) {
          targetGender = "All";
        } else if (course.gender === 0) {
          targetGender = "Male";
        } else if (course.gender === 1) {
          targetGender = "Female";
        }

        return { ...course, targetGender }; // Add the targetGender to the course object
      });

      setTotalPages(Math.ceil(courseData.totalCount / itemsPerPage));
      setCourses(processedCourses);
    } catch (error) {
      toast.error("Failed to fetch Course data. Some problem occurred. Please try again.")
    }
  };

  useEffect(() => {
    handleApiCallClick(currentPage);
  }, [currentPage]);

  const handleNextPage = () => {
    setCurrentPage((prevPage) => {
      if (prevPage < totalPages) {
        return prevPage + 1;
      }
      return prevPage;
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const value = event.target.value.toLowerCase();
    toast.success(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCurrentPage(1);
    handleApiCallClick(currentPage);
    toast.success("Search completed successfully!");
  };

  return (
    <div className="px-2 md:px-4 mt-6">
      <div className='flex flex-row items-center '>
        <h2 className="text-3xl font-bold text-blue-600 mb-5">Course</h2>

        <button
          type="button"
          className=" w-fit px-3 py-2 mx-3 mb-4 tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
          onClick={handleAddClick}
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>

      <form onSubmit={handleSearchSubmit} className="w-full md:flex-grow">
        <label
          htmlFor="default-search"
          className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
        >
          Search
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            type="search"
            id="default-search"
            className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Search by Name, Email, Mobile, Course name"
            value={searchTerm}
            onChange={handleSearchChange}
            required
          />
          <button
            type="submit"
            className="text-white absolute end-1.5 bottom-1.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            Search
          </button>
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-4 py-3.5 text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                Action
              </th>

              <th className="px-4 py-3.5 text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400">
                Course Name
              </th>
              <th className="px-4 py-3.5 text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400">
                Target Audience
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
            {courses.map((course) => (
              <tr key={course.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm whitespace-nowrap">
                  <div className="flex items-center gap-x-3">
                    <IconTrash
                      className="cursor-pointer text-gray-500 transition-colors duration-200 hover:text-red-500"
                      onClick={() => handleDeleteClick(course.id)}
                    />
                    <button
                      onClick={() => handleEditClick(course.id)}
                      className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">
                  {course.name}
                </td>
                <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">
                  {course.targetGender}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>



      {isModalOpen && (
        <DeleteItem onClose={() => setIsModalOpen(false)} onConfirm={confirmDelete} />
      )}

      <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
        {/* Previous Button */}
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
            />
          </svg>
          <span>Previous</span>
        </button>

        <div className="flex flex-wrap items-center justify-center gap-x-2">
          {/* Page Number */}

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-2 py-1 text-sm ${currentPage === index + 1
                ? "text-blue-500 bg-blue-100"
                : "text-gray-500 hover:bg-gray-100"
                } rounded-md`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {/* NEXT Button */}
        <button
          // disabled={currentPage === totalPages}
          // onClick={() => setCurrentPage(currentPage + 1)}
          onClick={handleNextPage}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <span>Next</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
            />
          </svg>
        </button>
      </div>

      <SideModal isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)}>
        <CourseForm onClose={() => setIsFormModalOpen(false)} courseId={editCourseId} url={url} 
        onSaved={() =>handleApiCallClick(currentPage)} />
      </SideModal>

    </div>
  );
}
